import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Send, CheckCircle, AlertCircle, Loader2, Calendar as CalendarIcon,
  Star, Mail, User
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { format } from 'date-fns';
import { usePageMeta } from '../hooks/usePageMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Toasts go to this page's own top-center Toaster, not the app-wide top-right
// one in App.js. A Toaster with an id shows only the toasts sent with that
// toasterId, and the app-wide one (no id) skips them, so each shows once.
const TOASTER_ID = 'public-form';

// Harmony Logo Component
const HarmonyLogo = ({ className = "w-16 h-16" }) => (
  <svg viewBox="0 0 800 600" className={className}>
    <path d="M 313.78 511.03 C290.56,523.39 270.87,533.58 270.03,533.67 C269.19,533.77 237.56,516.46 199.75,495.21 L 131.00 456.57 L 131.00 250.48 L 235.25 194.99 C292.58,164.47 343.21,137.56 347.75,135.20 L 356.00 130.89 L 356.00 488.55 ZM 244.41 168.07 C183.03,200.75 132.41,227.65 131.91,227.83 C131.37,228.03 131.01,210.21 131.02,184.33 L 131.05 140.50 L 242.65 78.00 C304.03,43.62 354.64,15.35 355.13,15.17 C355.62,14.98 356.00,35.37 356.00,61.73 L 356.00 108.63 ZM 355.00 548.00 C355.00,565.60 354.66,579.98 354.25,579.96 C352.30,579.87 296.06,547.54 296.73,546.90 C297.55,546.12 353.97,516.00 354.62,516.00 C354.83,516.00 355.00,530.40 355.00,548.00 Z" fill="rgb(13,192,254)"/>
    <path d="M 461.00 431.95 C417.27,455.22 380.71,474.67 379.75,475.17 C378.09,476.03 378.00,467.23 378.00,297.14 L 378.00 118.22 L 426.66 92.36 C453.43,78.14 475.75,66.63 476.27,66.79 C476.79,66.94 493.93,76.44 514.36,87.89 C534.79,99.34 553.97,110.08 557.00,111.76 C560.03,113.44 572.85,120.63 585.50,127.75 L 608.50 140.68 L 608.50 353.38 L 574.50 371.51 C555.80,381.49 504.73,408.68 461.00,431.95 ZM 490.29 515.84 C430.23,549.49 380.39,577.30 379.54,577.62 C378.17,578.15 378.00,573.78 378.00,537.70 L 378.00 497.19 L 436.75 465.95 C469.06,448.77 504.50,429.89 515.50,423.99 C553.15,403.81 605.50,376.11 607.26,375.44 C608.93,374.81 609.01,376.81 608.76,412.07 L 608.50 449.36 L 604.00 452.00 C601.53,453.45 550.36,482.18 490.29,515.84 ZM 408.50 76.91 C402.45,80.11 393.11,85.08 387.75,87.95 L 378.00 93.18 L 378.00 54.51 C378.00,31.97 378.38,15.97 378.90,16.17 C382.80,17.61 447.87,54.69 447.68,55.36 C447.45,56.15 439.56,60.49 408.50,76.91 Z" fill="rgb(254,199,25)"/>
    <path d="M 0.00 300.00 L 0.00 0.00 L 400.00 0.00 L 800.00 0.00 L 800.00 300.00 L 800.00 600.00 L 400.00 600.00 L 0.00 600.00 L 0.00 300.00 ZM 355.00 548.00 C355.00,530.40 354.83,516.00 354.62,516.00 C353.97,516.00 297.55,546.12 296.73,546.90 C296.06,547.54 352.30,579.87 354.25,579.96 C354.66,579.98 355.00,565.60 355.00,548.00 ZM 490.29 515.84 C550.36,482.18 601.53,453.45 604.00,452.00 L 608.50 449.36 L 608.76 412.07 C609.01,376.81 608.93,374.81 607.26,375.44 C605.50,376.11 553.15,403.81 515.50,423.99 C504.50,429.89 469.06,448.77 436.75,465.95 L 378.00 497.19 L 378.00 537.70 C378.00,573.78 378.17,578.15 379.54,577.62 C380.39,577.30 430.23,549.49 490.29,515.84 ZM 313.78 511.03 L 356.00 488.55 L 356.00 309.72 L 356.00 130.89 L 347.75 135.20 C343.21,137.56 292.58,164.47 235.25,194.99 L 131.00 250.48 L 131.00 353.52 L 131.00 456.57 L 199.75 495.21 C237.56,516.46 269.19,533.77 270.03,533.67 C270.87,533.58 290.56,523.39 313.78,511.03 ZM 461.00 431.95 C504.73,408.68 555.80,381.49 574.50,371.51 L 608.50 353.38 L 608.50 247.03 L 608.50 140.68 L 585.50 127.75 C572.85,120.63 560.03,113.44 557.00,111.76 C553.97,110.08 534.79,99.34 514.36,87.89 C493.93,76.44 476.79,66.94 476.27,66.79 C475.75,66.63 453.43,78.14 426.66,92.36 L 378.00 118.22 L 378.00 297.14 C378.00,467.23 378.09,476.03 379.75,475.17 C380.71,474.67 417.27,455.22 461.00,431.95 ZM 244.41 168.07 L 356.00 108.63 L 356.00 61.73 C356.00,35.37 355.62,14.98 355.13,15.17 C354.64,15.35 304.03,43.62 242.65,78.00 L 131.05 140.50 L 131.02 184.33 C131.01,210.21 131.37,228.03 131.91,227.83 C132.41,227.65 183.03,200.75 244.41,168.07 ZM 408.50 76.91 C439.56,60.49 447.45,56.15 447.68,55.36 C447.87,54.69 382.80,17.61 378.90,16.17 C378.38,15.97 378.00,31.97 378.00,54.51 L 378.00 93.18 L 387.75 87.95 C393.11,85.08 402.45,80.11 408.50,76.91 Z" fill="rgb(254,254,254)"/>
  </svg>
);

const PublicFormPage = () => {
  const { publicId } = useParams();
  
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [error, setError] = useState(null);
  
  // Form data state
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [nameReadonly, setNameReadonly] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [answers, setAnswers] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  
  // Set page title
  usePageMeta(
    form?.title ? `${form.title} | Harmony Form Builder` : 'Harmony Form Builder',
    '/favicon-formbuilder.svg'
  );

  // Fetch form data
  useEffect(() => {
    const fetchForm = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/public/forms/${publicId}`);
        setForm(response.data);
        
        // Initialize answers with empty values
        const initialAnswers = {};
        (response.data.fields || []).forEach(field => {
          if (!field.is_system_field) {
            if (field.type === 'checkbox' || field.type === 'multiselect') {
              initialAnswers[field.id] = [];
            } else {
              initialAnswers[field.id] = '';
            }
          }
        });
        setAnswers(initialAnswers);
      } catch (err) {
        const errorMsg = err.response?.data?.detail || 'Failed to load form';
        setError(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    if (publicId) {
      fetchForm();
    }
  }, [publicId]);

  // Employee lookup on email blur
  const handleEmailBlur = useCallback(async () => {
    if (!email || !email.includes('@')) return;
    
    setLookingUp(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/public/forms/employee-lookup?email=${encodeURIComponent(email)}`
      );
      
      if (response.data.found && response.data.name) {
        setName(response.data.name);
        setNameReadonly(true);
        toast.success(`Employee found! Name: ${response.data.name}`, { toasterId: TOASTER_ID });
      } else {
        setNameReadonly(false);
        // Don't clear name if user has already typed something
      }
    } catch (err) {
      console.error('Lookup failed:', err);
      setNameReadonly(false);
    } finally {
      setLookingUp(false);
    }
  }, [email]);

  // Evaluate conditional visibility
  const isFieldVisible = (field) => {
    if (!field.conditions || field.conditions.length === 0) return true;
    
    let result = true;
    for (let i = 0; i < field.conditions.length; i++) {
      const condition = field.conditions[i];
      const fieldValue = answers[condition.field_id];
      
      let conditionMet = false;
      switch (condition.operator) {
        case 'equals':
          conditionMet = fieldValue === condition.value;
          break;
        case 'not_equals':
          conditionMet = fieldValue !== condition.value;
          break;
        case 'contains':
          conditionMet = String(fieldValue || '').toLowerCase().includes(String(condition.value || '').toLowerCase());
          break;
        default:
          conditionMet = true;
      }
      
      if (i === 0) {
        result = conditionMet;
      } else {
        if (condition.logic === 'AND') {
          result = result && conditionMet;
        } else {
          result = result || conditionMet;
        }
      }
    }
    
    return result;
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!email || !email.includes('@')) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!name || !name.trim()) {
      errors.name = 'Name is required';
    }
    
    // Validate custom fields
    (form?.fields || []).forEach(field => {
      if (field.is_system_field) return;
      if (!isFieldVisible(field)) return;
      
      if (field.required) {
        const value = answers[field.id];
        if (value === undefined || value === null || value === '' || 
            (Array.isArray(value) && value.length === 0)) {
          errors[field.id] = `${field.label} is required`;
        }
      }
      
      // Number validation
      if (field.type === 'number' && answers[field.id] !== '' && answers[field.id] !== undefined) {
        const numValue = Number(answers[field.id]);
        if (field.min_value !== null && field.min_value !== undefined && numValue < field.min_value) {
          errors[field.id] = `Minimum value is ${field.min_value}`;
        }
        if (field.max_value !== null && field.max_value !== undefined && numValue > field.max_value) {
          errors[field.id] = `Maximum value is ${field.max_value}`;
        }
      }
    });
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors before submitting', { toasterId: TOASTER_ID });
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/public/forms/${publicId}/submit`,
        {
          email: email.toLowerCase(),
          name,
          answers
        }
      );
      
      setSubmitted(true);
      setSubmissionResult(response.data);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to submit form';
      toast.error(errorMsg, { toasterId: TOASTER_ID });
    } finally {
      setSubmitting(false);
    }
  };

  // Update answer
  const updateAnswer = (fieldId, value) => {
    setAnswers(prev => ({ ...prev, [fieldId]: value }));
    // Clear validation error when user starts typing
    if (validationErrors[fieldId]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  };

  // Render field based on type
  const renderField = (field) => {
    if (field.is_system_field) return null;
    if (!isFieldVisible(field)) return null;
    
    const error = validationErrors[field.id];
    const value = answers[field.id];
    
    switch (field.type) {
      case 'text':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-slate-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              value={value || ''}
              onChange={(e) => updateAnswer(field.id, e.target.value)}
              placeholder={field.placeholder || ''}
              className={error ? 'border-red-500' : ''}
              data-testid={`field-${field.id}`}
            />
            {field.help_text && (
              <p className="text-xs text-slate-500">{field.help_text}</p>
            )}
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        );

      case 'paragraph':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-slate-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Textarea
              value={value || ''}
              onChange={(e) => updateAnswer(field.id, e.target.value)}
              placeholder={field.placeholder || ''}
              rows={4}
              className={error ? 'border-red-500' : ''}
              data-testid={`field-${field.id}`}
            />
            {field.help_text && (
              <p className="text-xs text-slate-500">{field.help_text}</p>
            )}
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        );

      case 'number':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-slate-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type="number"
              value={value || ''}
              onChange={(e) => updateAnswer(field.id, e.target.value)}
              placeholder={field.placeholder || ''}
              min={field.min_value}
              max={field.max_value}
              className={error ? 'border-red-500' : ''}
              data-testid={`field-${field.id}`}
            />
            {field.help_text && (
              <p className="text-xs text-slate-500">{field.help_text}</p>
            )}
            {(field.min_value !== null || field.max_value !== null) && (
              <p className="text-xs text-slate-400">
                {field.min_value !== null && `Min: ${field.min_value}`}
                {field.min_value !== null && field.max_value !== null && ' | '}
                {field.max_value !== null && `Max: ${field.max_value}`}
              </p>
            )}
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        );

      case 'radio':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-slate-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <RadioGroup
              value={value || ''}
              onValueChange={(v) => updateAnswer(field.id, v)}
              className={error ? 'border border-red-500 rounded-lg p-2' : ''}
              data-testid={`field-${field.id}`}
            >
              {(field.options || []).map((option, idx) => (
                <div key={idx} className="flex items-center space-x-2">
                  <RadioGroupItem value={option} id={`${field.id}-${idx}`} />
                  <Label htmlFor={`${field.id}-${idx}`} className="font-normal cursor-pointer">
                    {option}
                  </Label>
                </div>
              ))}
            </RadioGroup>
            {field.help_text && (
              <p className="text-xs text-slate-500">{field.help_text}</p>
            )}
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        );

      case 'checkbox':
      case 'multiselect':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-slate-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className={`space-y-2 ${error ? 'border border-red-500 rounded-lg p-2' : ''}`}>
              {(field.options || []).map((option, idx) => (
                <div key={idx} className="flex items-center space-x-2">
                  <Checkbox
                    id={`${field.id}-${idx}`}
                    checked={(value || []).includes(option)}
                    onCheckedChange={(checked) => {
                      const newValue = checked
                        ? [...(value || []), option]
                        : (value || []).filter(v => v !== option);
                      updateAnswer(field.id, newValue);
                    }}
                    data-testid={`field-${field.id}-${idx}`}
                  />
                  <Label htmlFor={`${field.id}-${idx}`} className="font-normal cursor-pointer">
                    {option}
                  </Label>
                </div>
              ))}
            </div>
            {field.help_text && (
              <p className="text-xs text-slate-500">{field.help_text}</p>
            )}
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        );

      case 'dropdown':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-slate-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Select
              value={value || ''}
              onValueChange={(v) => updateAnswer(field.id, v)}
            >
              <SelectTrigger 
                className={error ? 'border-red-500' : ''}
                data-testid={`field-${field.id}`}
              >
                <SelectValue placeholder={field.placeholder || 'Select an option'} />
              </SelectTrigger>
              <SelectContent>
                {(field.options || []).map((option, idx) => (
                  <SelectItem key={idx} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {field.help_text && (
              <p className="text-xs text-slate-500">{field.help_text}</p>
            )}
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        );

      case 'date':
        const dateValue = value ? new Date(value) : undefined;
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-slate-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={`w-full justify-start text-left font-normal ${
                    !value && 'text-muted-foreground'
                  } ${error ? 'border-red-500' : ''}`}
                  data-testid={`field-${field.id}`}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateValue ? format(dateValue, 'PPP') : (field.placeholder || 'Pick a date')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateValue}
                  onSelect={(date) => updateAnswer(field.id, date ? date.toISOString().split('T')[0] : '')}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {field.help_text && (
              <p className="text-xs text-slate-500">{field.help_text}</p>
            )}
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        );

      case 'rating':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-slate-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div 
              className={`flex gap-1 ${error ? 'p-2 border border-red-500 rounded-lg inline-flex' : ''}`}
              data-testid={`field-${field.id}`}
            >
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  type="button"
                  onClick={() => updateAnswer(field.id, star)}
                  className="text-2xl transition-transform hover:scale-110 focus:outline-none"
                >
                  <Star 
                    className={`w-8 h-8 ${
                      star <= (value || 0) 
                        ? 'fill-amber-400 text-amber-400' 
                        : 'fill-none text-slate-300'
                    }`} 
                  />
                </button>
              ))}
            </div>
            {field.help_text && (
              <p className="text-xs text-slate-500">{field.help_text}</p>
            )}
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        );

      default:
        return null;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardHeader className="text-center">
            <Skeleton className="h-8 w-48 mx-auto mb-2" />
            <Skeleton className="h-4 w-72 mx-auto" />
          </CardHeader>
          <CardContent className="space-y-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <CardTitle className="text-red-600">Form Not Available</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Form closed state
  if (form?.is_closed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <HarmonyLogo className="w-20 h-20 mx-auto mb-4" />
            <CardTitle>Form Closed</CardTitle>
            <CardDescription>
              This form is no longer accepting responses.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Success state - Bug 2 Fix: Remove "Submit Another Response" option
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <HarmonyLogo className="w-20 h-20 mx-auto mb-4" />
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <CardTitle className="text-green-600">
              {submissionResult?.is_update ? 'Response Updated!' : 'Response Submitted!'}
            </CardTitle>
            <CardDescription>
              {submissionResult?.message || 'Thank you for your submission.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Badge variant="outline" className="text-lg py-2 px-4">
              Version {submissionResult?.version || 1}
            </Badge>
            <p className="text-sm text-slate-500 mt-6">
              You can close this window now.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Get custom fields
  const customFields = (form?.fields || []).filter(f => !f.is_system_field);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 py-8 px-4" data-testid="public-form-page">
      <div className="max-w-2xl mx-auto">
        {/* Header with Harmony Logo - Bug 5 Fix */}
        <div className="text-center mb-8">
          <HarmonyLogo className="w-20 h-20 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900">{form?.title}</h1>
          {form?.description && (
            <p className="text-slate-600 mt-2">{form.description}</p>
          )}
        </div>

        {/* Form */}
        <Card className="shadow-lg">
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* System Fields */}
              <div className="space-y-2">
                <Label className="text-slate-700">
                  Email ID <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={handleEmailBlur}
                    placeholder="Enter your email address"
                    className={`pl-10 ${validationErrors.email ? 'border-red-500' : ''}`}
                    data-testid="email-field"
                  />
                  {lookingUp && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-orange-500" />
                  )}
                </div>
                <p className="text-xs text-slate-500">Your official email address</p>
                {validationErrors.email && (
                  <p className="text-xs text-red-500">{validationErrors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-slate-700">
                  Full Name <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={name}
                    onChange={(e) => !nameReadonly && setName(e.target.value)}
                    placeholder={nameReadonly ? '' : 'Enter your full name'}
                    className={`pl-10 ${nameReadonly ? 'bg-green-50 border-green-300' : ''} ${validationErrors.name ? 'border-red-500' : ''}`}
                    readOnly={nameReadonly}
                    data-testid="name-field"
                  />
                </div>
                {nameReadonly && (
                  <p className="text-xs text-green-600">✓ Auto-populated from employee records</p>
                )}
                {!nameReadonly && (
                  <p className="text-xs text-slate-500">Enter your full name</p>
                )}
                {validationErrors.name && (
                  <p className="text-xs text-red-500">{validationErrors.name}</p>
                )}
              </div>

              {/* Custom Fields */}
              {customFields.map(field => renderField(field))}

              {/* Submit Button */}
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
                disabled={submitting}
                data-testid="submit-form-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Response
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-sm text-slate-500 mt-6">
          Powered by Harmony Apps
        </p>
      </div>
      
      <Toaster id={TOASTER_ID} position="top-center" />
    </div>
  );
};

export default PublicFormPage;
